// アプリのエントリ + コントローラ。シェル・ルーティング・相場取得・セット操作を配線する。
import './styles/tokens.css';
import './styles/app.css';
import { fetchCatalog, fetchLeagues } from './api/worker-client';
import type { Poe2ScoutLeague } from './api/types';
import { RateTable } from './currency/rate-table';
import { createFarmSet } from './store/factory';
import { exportSet, importSets } from './store/io';
import {
  deleteSet,
  getActiveLeague,
  loadSets,
  newId,
  saveSet,
  setActiveLeague,
} from './store/persist';
import type { FarmSet } from './store/schema';
import { CalcClient } from './worker-thread/calc-client';
import { AppStore } from './ui/state';
import { el, mount } from './ui/dom';
import { onRouteChange, currentRoute, navigate, type Route } from './ui/router';
import { CalcView } from './ui/views/calc-view';
import { CompareView } from './ui/views/compare-view';
import { ExplainView } from './ui/views/explain-view';
import { MeasureView } from './ui/views/measure-view';
import { MeasureController } from './ui/measure-state';

function pickCurrentLeague(leagues: Poe2ScoutLeague[]): string | null {
  const sc = leagues.find((l) => l.IsCurrent && !l.Value.startsWith('HC '));
  if (sc) return sc.Value;
  const any = leagues.find((l) => l.IsCurrent);
  return any?.Value ?? leagues[0]?.Value ?? null;
}

function toast(msg: string, kind: 'ok' | 'err' = 'ok'): void {
  const t = el('div', {
    style: `position:fixed;left:50%;bottom:24px;transform:translateX(-50%);z-index:50;padding:10px 16px;border-radius:8px;font-size:.9rem;border:1px solid ${kind === 'err' ? '#9c3a2e' : '#4f8b97'};background:#161924;color:#ece6d9;box-shadow:0 8px 30px -12px rgba(0,0,0,.6)`,
  }, msg);
  document.body.append(t);
  setTimeout(() => t.remove(), 3600);
}

class App {
  private readonly calc = new CalcClient();
  private store!: AppStore;
  private view!: CalcView;
  private compareView!: CompareView;
  private explainView!: ExplainView;
  private measureView!: MeasureView;
  private readonly measure = new MeasureController();
  private leagues: Poe2ScoutLeague[] = [];
  private calcEl = el('div');
  private measureEl = el('div');
  private compareEl = el('div');
  private explainEl = el('div');
  private navButtons = new Map<Route, HTMLButtonElement>();

  async start(root: HTMLElement): Promise<void> {
    this.explainView = new ExplainView(this.explainEl);
    this.buildShell(root);

    // リーグ → アクティブリーグ → セット。
    try {
      this.leagues = await fetchLeagues();
    } catch (e) {
      mount(this.calcEl, el('div', { class: 'placeholder' }, `相場サーバに接続できません: ${String(e)}`));
      return;
    }
    const persisted = getActiveLeague();
    const active =
      persisted && this.leagues.some((l) => l.Value === persisted) ? persisted : pickCurrentLeague(this.leagues);
    if (!active) {
      mount(this.calcEl, el('div', { class: 'placeholder' }, 'リーグが取得できませんでした。'));
      return;
    }
    setActiveLeague(active);

    const sets = loadSets(active);
    const initial = sets[0] ?? createFarmSet(active);

    this.store = new AppStore(this.calc, initial, 280);
    this.store.setLeagues(this.leagues);
    this.view = new CalcView(this.calcEl, this.store, this.callbacks());
    this.view.mount();
    this.compareView = new CompareView(
      this.compareEl,
      this.store,
      (input) => this.calc.overview(input, { N: 40_000, T: 200 }),
      {
        getSets: () => loadSets(this.store.activeLeague ?? ''),
        onOpenInCalc: (set) => { this.store.loadSet(set); navigate('calc'); },
        onDuplicate: (id) => {
          const src = loadSets(this.store.activeLeague ?? '').find((s) => s.id === id);
          if (!src) return;
          const copy: FarmSet = JSON.parse(JSON.stringify(src));
          copy.id = newId();
          copy.name = `${copy.name} のコピー`;
          saveSet(copy);
        },
        onDelete: (id) => {
          const league = this.store.activeLeague ?? '';
          deleteSet(league, id);
          if (this.store.currentSet.id === id) {
            const rest = loadSets(league);
            this.store.loadSet(rest[0] ?? createFarmSet(league, this.store.currentSet.displayUnit));
          }
        },
      },
    );

    this.measureView = new MeasureView(this.measureEl, this.store, this.measure, {
      savedSets: () => loadSets(this.store.activeLeague ?? ''),
      toast,
      applyToSet: <T,>(setId: string, fn: (set: FarmSet) => T): T | null => {
        // 計算ビューで同セットを開いていれば store 経由（署名変化→自動再計算）。
        if (this.store.currentSet.id === setId) {
          let out!: T;
          this.store.updateSet((s) => { out = fn(s); });
          saveSet(this.store.currentSet);
          this.view.refreshInputs();
          return out;
        }
        const set = loadSets(this.store.activeLeague ?? '').find((x) => x.id === setId);
        if (!set) return null;
        const out = fn(set);
        saveSet(set);
        return out;
      },
    });
    this.measureView.mount();
    this.measure.onLeagueChanged(active);

    await this.loadRates(active);
    this.applyRoute(currentRoute());
  }

  private buildShell(root: HTMLElement): void {
    const nav = el('nav', { class: 'nav' });
    for (const [route, label] of [['calc', '計算'], ['measure', '測定'], ['compare', '比較'], ['explain', '解説']] as const) {
      const b = el('button', { onclick: () => navigate(route) }, label) as HTMLButtonElement;
      this.navButtons.set(route, b);
      nav.append(b);
    }
    mount(
      root,
      el('div', { class: 'shell' }, [
        el('header', { class: 'topbar' }, [
          el('div', { class: 'brand' }, [
            el('b', {}, [document.createTextNode('PoE2 '), el('span', { class: 'accent' }, 'ファーム考察')]),
            el('small', {}, 'KELLY BANKROLL DESK'),
          ]),
          nav,
        ]),
        this.calcEl,
        this.measureEl,
        this.compareEl,
        this.explainEl,
        el('footer', { class: 'foot' }, [
          el('span', {}, 'base = Exalted Orb ・ 相場 poe2scout'),
          el('a', { href: '#/explain' }, '数学・前提・限界 →'),
        ]),
      ]),
    );
    onRouteChange((r) => this.applyRoute(r));
  }

  private applyRoute(r: Route): void {
    this.calcEl.style.display = r === 'calc' ? '' : 'none';
    this.measureEl.style.display = r === 'measure' ? '' : 'none';
    this.compareEl.style.display = r === 'compare' ? '' : 'none';
    this.explainEl.style.display = r === 'explain' ? '' : 'none';
    for (const [route, btn] of this.navButtons) btn.setAttribute('aria-current', String(route === r));
    if (r === 'compare') {
      void this.compareView.show();
    }
    if (r === 'explain') {
      const anchor = location.hash.split('/')[2];
      this.explainView.scrollTo(anchor);
    }
  }

  private async loadRates(league: string): Promise<void> {
    try {
      const catalog = await fetchCatalog(league);
      this.store.setRateTable(RateTable.buildCatalog(catalog, league, Date.now()));
    } catch (e) {
      toast(`相場の取得に失敗: ${String(e)}`, 'err');
    }
  }

  private callbacks() {
    const self = this;
    return {
      async onLeagueChange(league: string) {
        setActiveLeague(league);
        const sets = loadSets(league);
        const next = sets[0] ?? createFarmSet(league, self.store.currentSet.displayUnit);
        self.store.loadSet(next);
        self.measure.onLeagueChanged(league);
        await self.loadRates(league);
      },
      async onRefetchRates() {
        await self.loadRates(self.store.activeLeague ?? '');
        toast('相場を再取得しました（market 行のみ更新）');
      },
      onSave() {
        const saved = saveSet(self.store.currentSet);
        self.store.currentSet.id = saved.id;
        toast(`「${saved.name}」を保存しました`);
        self.view.refreshInputs();
      },
      onLoad(id: string) {
        const set = loadSets(self.store.activeLeague ?? '').find((s) => s.id === id);
        if (set) self.store.loadSet(set);
      },
      onNew() {
        self.store.loadSet(createFarmSet(self.store.activeLeague ?? '', self.store.currentSet.displayUnit));
      },
      onDuplicate() {
        const copy: FarmSet = JSON.parse(JSON.stringify(self.store.currentSet));
        copy.id = newId();
        copy.name = `${copy.name} のコピー`;
        saveSet(copy);
        self.store.loadSet(copy);
        toast('複製しました');
      },
      onDelete() {
        const cur = self.store.currentSet;
        deleteSet(cur.league, cur.id);
        const rest = loadSets(cur.league);
        self.store.loadSet(rest[0] ?? createFarmSet(cur.league, cur.displayUnit));
        toast('削除しました');
      },
      onExport() {
        const json = exportSet(self.store.currentSet);
        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = el('a', { href: url, download: `${self.store.currentSet.name || 'farmset'}.json` });
        a.click();
        URL.revokeObjectURL(url);
      },
      onImport(file: File) {
        const reader = new FileReader();
        reader.onload = () => {
          const res = importSets(String(reader.result), {
            targetLeague: self.store.activeLeague ?? '',
            reassign: true,
          });
          if (res.imported[0]) {
            self.store.loadSet(res.imported[0]);
            toast(`${res.imported.length} 件を取り込みました`);
          } else {
            toast(`取込に失敗: ${res.errors[0] ?? '不明なエラー'}`, 'err');
          }
        };
        reader.readAsText(file);
      },
      savedSets() {
        return loadSets(self.store.activeLeague ?? '').map((s) => ({ id: s.id, name: s.name }));
      },
    };
  }
}

const root = document.querySelector<HTMLDivElement>('#app');
if (root) void new App().start(root);
